import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { supplierProductsEndpointIdentifier } from 'apis/supplier/use-get-supplier-products-api';
import type { AxiosError } from 'axios';
import { useQueryClient, type UseMutationOptions, type UseMutationResult } from 'react-query';

export const uploadSupplierProductsSheetEndpointIdentifier =
  'ums/v1/cms/exclusive-deals/suppliers/products/sheet/upload';

const buildEndpoint = (supplierId: number) =>
  `ums/v1/cms/exclusive-deals/suppliers/${supplierId}/products/sheet/upload`;

export interface SupplierProductsUploadResultDto {
  upserted: number;
  created: number;
  updated: number;
  imagesQueued: number;
}

export interface SupplierProductsUploadRowError {
  row: number;
  column: string;
  reason: string;
}

export interface SupplierProductsUploadErrorDto {
  statusCode?: number;
  message: string;
  // All-or-nothing: a 400 carries structured per-row reasons here. Nothing is written.
  errors?: SupplierProductsUploadRowError[];
}

// NOTE: this endpoint returns the result un-wrapped (no `data` key).
type TData = SupplierProductsUploadResultDto;
type TError = AxiosError<SupplierProductsUploadErrorDto>;
type TVariables = { supplierId: number; body: FormData };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Upload a supplier's catalog Excel sheet (products + images) via
 * POST /ums/v1/cms/exclusive-deals/suppliers/{supplierId}/products/sheet/upload (multipart, field `file`).
 * id-driven upsert: blank id → create, existing id → update (must belong to the supplier).
 * All-or-nothing: a 400 carries structured `errors: { row, column, reason }[]` and nothing is written.
 * External image_url values are re-hosted to S3 asynchronously after the response returns.
 */
export function useUploadSupplierProductsSheetApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async ({ supplierId, body }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.post(buildEndpoint(supplierId), body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      // Per-row errors are rendered in the modal; show the summary message as a toast.
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [uploadSupplierProductsSheetEndpointIdentifier],
    onSuccess: (data: TData) => {
      // Catalog changed — refresh the products dropdown the bulk-edit grid reads from.
      queryClient.invalidateQueries([supplierProductsEndpointIdentifier]);
      const { created, updated, imagesQueued } = data;
      const imageNote = imagesQueued ? `, ${imagesQueued} image(s) queued` : '';
      toast({ description: `Uploaded: ${created} created, ${updated} updated${imageNote}`, status: 'success' });
    },
    ...options,
  });
}
